import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, Profile } from '@/types';
import {
  getCurrentUser,
  signOut as arcadiaSignOut,
  signInWithEmail,
  signInWithGoogleToken,
  signUpWithEmail,
  onAuthStateChange,
} from '@/lib/auth/session';

let cachedUser: { user: AuthUser; profile: Profile } | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000;

/** Bumped when the session refresh runs so stale `getCurrentUser()` results cannot wipe fresh auth. */
let sessionRevision = 0;

export interface AuthContextValue {
  user: AuthUser | null;
  profile: Profile | null;
  loading: boolean;
  /** Refetch `/api/auth/me` and update cached profile (e.g. after spending points). */
  refreshSession: () => Promise<void>;
  signOut: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInGoogle: (idToken: string) => Promise<void>;
  isAuthenticated: boolean;
}

const ArcadiaAuthContext = createContext<AuthContextValue | null>(null);

/**
 * Single source of truth for Arcadia session.
 * Must wrap the tree once — multiple `useAuth()` consumers share this state.
 */
export function ArcadiaAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(cachedUser?.user || null);
  const [profile, setProfile] = useState<Profile | null>(cachedUser?.profile || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const myRevision = ++sessionRevision;
    setLoading(true);

    async function checkSession() {
      try {
        const currentUser = await getCurrentUser();

        if (!mounted || sessionRevision !== myRevision) {
          if (mounted) setLoading(false);
          return;
        }

        const now = Date.now();
        if (cachedUser && now - cacheTimestamp < CACHE_DURATION) {
          if (mounted) {
            setUser(cachedUser.user);
            setProfile(cachedUser.profile);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          if (currentUser) {
            cachedUser = currentUser;
            cacheTimestamp = now;
            setUser(currentUser.user);
            setProfile(currentUser.profile);
          } else {
            cachedUser = null;
            cacheTimestamp = 0;
            setUser(null);
            setProfile(null);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to check session:', error);
        if (mounted && sessionRevision === myRevision) {
          setLoading(false);
        }
      }
    }

    void checkSession();

    const unsubscribe = onAuthStateChange((authData) => {
      if (!mounted) return;
      if (authData) {
        cachedUser = authData;
        cacheTimestamp = Date.now();
        setUser(authData.user);
        setProfile(authData.profile);
      } else {
        cachedUser = null;
        cacheTimestamp = 0;
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await arcadiaSignOut();
      cachedUser = null;
      cacheTimestamp = 0;
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      cachedUser = null;
      cacheTimestamp = 0;
      setUser(null);
      setProfile(null);
      await arcadiaSignOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
      await handleSignOut();
    }
  }, [handleSignOut]);

  const signInEmailHandler = useCallback(async (email: string, password: string) => {
    const authData = await signInWithEmail(email, password);
    cachedUser = authData;
    cacheTimestamp = Date.now();
    setUser(authData.user);
    setProfile(authData.profile);
  }, []);

  const signUpEmailHandler = useCallback(async (email: string, password: string, displayName?: string) => {
    const authData = await signUpWithEmail(email, password, displayName);
    cachedUser = authData;
    cacheTimestamp = Date.now();
    setUser(authData.user);
    setProfile(authData.profile);
  }, []);

  const signInGoogleHandler = useCallback(async (idToken: string) => {
    const authData = await signInWithGoogleToken(idToken);
    cachedUser = authData;
    cacheTimestamp = Date.now();
    setUser(authData.user);
    setProfile(authData.profile);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        cachedUser = currentUser;
        cacheTimestamp = Date.now();
        setUser(currentUser.user);
        setProfile(currentUser.profile);
      }
    } catch (error) {
      console.error('refreshSession failed:', error);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      refreshSession,
      signOut,
      signInEmail: signInEmailHandler,
      signUpEmail: signUpEmailHandler,
      signInGoogle: signInGoogleHandler,
      isAuthenticated: !!user && !!profile,
    }),
    [
      user,
      profile,
      loading,
      refreshSession,
      signOut,
      signInEmailHandler,
      signUpEmailHandler,
      signInGoogleHandler,
    ]
  );

  return <ArcadiaAuthContext.Provider value={value}>{children}</ArcadiaAuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(ArcadiaAuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <ArcadiaAuthProvider>');
  }
  return ctx;
}
